import React, { useState } from "react";
import { Droplet, Info } from "lucide-react";
import { exportCalculatorPDF } from "../../lib/pdfExport";
import { SavePdfButton } from "./SavePdfButton";

// ISF — Insulin Sensitivity (correction) Factor
//   ISF (mmol/L per 1 unit) = 100 / TDD
//   Correction dose = (current BG − target BG) / ISF
export default function ISFCalc({ lang }) {
  const [tdd, setTdd] = useState("40");
  const [current, setCurrent] = useState("12");
  const [target, setTarget] = useState("6");

  const tddN = parseFloat(tdd);
  const cur = parseFloat(current);
  const tgt = parseFloat(target);
  const validTdd = !isNaN(tddN) && tddN >= 5 && tddN <= 200;
  const validBG = !isNaN(cur) && !isNaN(tgt) && cur >= 2 && cur <= 35 && tgt >= 3 && tgt <= 12;

  const isf = validTdd ? Math.round((100 / tddN) * 10) / 10 : null;
  const correction =
    isf && validBG && cur > tgt ? Math.round(((cur - tgt) / isf) * 10) / 10 : isf && validBG ? 0 : null;

  return (
    <section className="card-clinic !p-7" data-testid="isf-calculator">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-teal-200/40 text-teal-500 flex items-center justify-center">
          <Droplet className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-surface-ink">
            {lang === "et"
              ? "ISF — insuliini tundlikkuse tegur"
              : lang === "ru"
              ? "ISF — фактор чувствительности к инсулину"
              : "ISF — insulin sensitivity factor"}
          </h2>
          <p className="text-[15px] text-surface-inkSoft">
            {lang === "et"
              ? "1 TÜ kiiretoimelist insuliini langetab veresuhkrut X mmol/L võrra. Arvutus reegli 100 järgi."
              : lang === "ru"
              ? "1 ед быстродействующего инсулина снижает глюкозу на X mmol/L. По правилу 100."
              : "1 unit of rapid-acting insulin lowers blood glucose by X mmol/L. Based on the 100 rule."}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-[13px] font-medium text-surface-ink mb-1.5">
            {lang === "et"
              ? "Päevane insuliini koguannus (TDD), TÜ"
              : lang === "ru"
              ? "Суточная доза инсулина (TDD), ед"
              : "Total daily insulin dose (TDD), units"}
          </label>
          <input
            type="number"
            step="0.5"
            min="5"
            max="200"
            value={tdd}
            onChange={(e) => setTdd(e.target.value)}
            className="input w-full text-[16px] font-medium"
            data-testid="isf-tdd-input"
          />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-surface-ink mb-1.5">
            {lang === "et"
              ? "Praegune veresuhkur (mmol/L)"
              : lang === "ru"
              ? "Текущая глюкоза (mmol/L)"
              : "Current blood glucose (mmol/L)"}
          </label>
          <input
            type="number"
            step="0.1"
            min="2"
            max="35"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="input w-full text-[16px] font-medium"
            data-testid="isf-current-input"
          />
        </div>
        <div>
          <label className="block text-[13px] font-medium text-surface-ink mb-1.5">
            {lang === "et"
              ? "Sihtväärtus (mmol/L)"
              : lang === "ru"
              ? "Целевая глюкоза (mmol/L)"
              : "Target glucose (mmol/L)"}
          </label>
          <input
            type="number"
            step="0.1"
            min="3"
            max="12"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="input w-full text-[16px] font-medium"
            data-testid="isf-target-input"
          />
        </div>
      </div>

      {isf && (
        <div
          className="mt-5 rounded-xl border border-teal-300 bg-teal-200/30 p-4 grid grid-cols-1 sm:grid-cols-2 gap-4"
          data-testid="isf-result"
        >
          <div>
            <div className="text-[11px] uppercase tracking-wider text-surface-inkSoft">ISF</div>
            <div className="text-3xl font-bold text-surface-ink">
              1 : {isf}
            </div>
            <div className="text-[14px] font-semibold mt-1 text-teal-600">
              {lang === "et"
                ? `1 TÜ langetab veresuhkrut ~${isf} mmol/L`
                : lang === "ru"
                ? `1 ед снижает глюкозу ~${isf} mmol/L`
                : `1 unit lowers BG by ~${isf} mmol/L`}
            </div>
          </div>
          {correction !== null && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-surface-inkSoft">
                {lang === "et"
                  ? "Soovituslik korrektsiooniannus"
                  : lang === "ru"
                  ? "Рекомендуемая коррекция"
                  : "Suggested correction dose"}
              </div>
              <div className="text-3xl font-bold text-surface-ink">
                {correction} <span className="text-base font-medium">TÜ</span>
              </div>
              <div className="text-[14px] text-surface-inkSoft mt-1">
                {correction === 0
                  ? lang === "et"
                    ? "Veresuhkur sihtväärtuses — korrektsioon ei ole vajalik"
                    : lang === "ru"
                    ? "Глюкоза в целевом диапазоне — коррекция не нужна"
                    : "Glucose at target — no correction needed"
                  : lang === "et"
                  ? `(${cur} − ${tgt}) ÷ ${isf}`
                  : `(${cur} − ${tgt}) ÷ ${isf}`}
              </div>
            </div>
          )}
        </div>
      )}

      {isf && (
        <SavePdfButton
          lang={lang}
          testId="isf-save-pdf"
          onClick={() =>
            exportCalculatorPDF({
              lang,
              title:
                lang === "et"
                  ? "ISF — insuliini tundlikkuse tegur"
                  : lang === "ru"
                  ? "ISF — фактор чувствительности к инсулину"
                  : "ISF — insulin sensitivity factor",
              inputs: [
                {
                  label:
                    lang === "et"
                      ? "Päevane insuliini koguannus (TDD)"
                      : lang === "ru"
                      ? "Суточная доза инсулина (TDD)"
                      : "Total daily insulin dose (TDD)",
                  value: `${tddN} TÜ`,
                },
                ...(validBG
                  ? [
                      {
                        label:
                          lang === "et"
                            ? "Praegune veresuhkur"
                            : lang === "ru"
                            ? "Текущая глюкоза"
                            : "Current blood glucose",
                        value: `${cur} mmol/L`,
                      },
                      {
                        label:
                          lang === "et"
                            ? "Sihtväärtus"
                            : lang === "ru"
                            ? "Целевая глюкоза"
                            : "Target glucose",
                        value: `${tgt} mmol/L`,
                      },
                    ]
                  : []),
              ],
              results: [
                {
                  label: "ISF",
                  value: `1 : ${isf}`,
                  hint:
                    lang === "et"
                      ? `1 TÜ langetab veresuhkrut ~${isf} mmol/L`
                      : lang === "ru"
                      ? `1 ед снижает глюкозу ~${isf} mmol/L`
                      : `1 unit lowers BG by ~${isf} mmol/L`,
                },
                ...(correction !== null
                  ? [
                      {
                        label:
                          lang === "et"
                            ? "Soovituslik korrektsiooniannus"
                            : lang === "ru"
                            ? "Рекомендуемая коррекция"
                            : "Suggested correction dose",
                        value: `${correction} TÜ`,
                        hint:
                          correction === 0
                            ? lang === "et"
                              ? "Veresuhkur sihtväärtuses — korrektsioon ei ole vajalik"
                              : lang === "ru"
                              ? "Глюкоза в целевом диапазоне"
                              : "Glucose at target"
                            : `(${cur} − ${tgt}) / ${isf}`,
                      },
                    ]
                  : []),
              ],
              note:
                lang === "et"
                  ? "Reegel 100 sobib kiiretoimelisele insuliinile. Ära stäkki korrektsiooniboolusi — eelmise boolus võib veel toimida (insulin on board). Hüpoglükeemia korral (< 4 mmol/L) ÄRA tee korrektsiooniboolust. Kohanda ISF-i koos arstiga."
                  : lang === "ru"
                  ? "Правило 100 — для быстродействующего инсулина. Не складывайте коррекционные болюсы. При гипогликемии (< 4 mmol/L) НЕ вводите коррекцию."
                  : "The 100 rule applies to rapid-acting insulin. Do not stack corrections. If BG < 4 mmol/L, do NOT take a correction.",
            })
          }
        />
      )}

      <div className="mt-5 flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-300/60 p-4 text-[14px] leading-relaxed text-surface-ink">
        <Info className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <p>
          {lang === "et"
            ? "Reegel 100 sobib kiiretoimelisele insuliinile (lispro, aspart, glulisiin) ja annab keskmise mmol/L muutuse 1 TÜ kohta. Ära stäkki korrektsiooniboolusi — eelmise boolus võib veel toimida (insulin on board). Hüpoglükeemia korral (< 4 mmol/L) ÄRA tee korrektsiooniboolust. Kohanda ISF-i koos arstiga."
            : lang === "ru"
            ? "Правило 100 — для быстродействующего инсулина. Не складывайте коррекционные болюсы (учитывайте активный инсулин). При гипогликемии (< 4 mmol/L) НЕ вводите коррекцию. Корректируйте ISF с врачом."
            : "The 100 rule applies to rapid-acting insulin. Do not stack corrections — consider insulin on board. If BG < 4 mmol/L, do NOT take a correction. Tune ISF with your physician."}
        </p>
      </div>
    </section>
  );
}
